import { useState, useEffect } from 'react';
import {
  useSelector,
  type TypedUseSelectorHook,
  useDispatch,
} from 'react-redux';
import { useLocation } from 'react-router-dom';
import cx from 'classnames';
import { defineMessages, useIntl } from 'react-intl';

import {
  ListBox,
  ListBoxItem,
  Popover,
  Select,
  Button,
  SelectValue,
  Label,
  TextField,
  Input,
  TextArea,
  FieldError,
  RadioGroup,
  Radio,
  type TextFieldProps,
} from 'react-aria-components';

import config from '@plone/registry';
import ArrayWidget from '@plone/volto/components/manage/Widgets/ArrayWidget';

import ChevronDownSVG from '@redturtle/qubicaamf-theme/components/Icons/ChevronDownSVG';

import styles from '@redturtle/qubicaamf-theme/components/View/FormView/styles.module.css';
import '@plone/components/src/styles/basic/Select.css';
import type { FormProps } from '@redturtle/qubicaamf-theme/components/View/FormView/Components/NewInvestmentForm';

import { getContactFormInfo } from '@redturtle/qubicaamf-theme/actions';
import type { ContactFormInfoInitialState } from '@redturtle/qubicaamf-theme/reducers';

interface FormSectionProps extends FormProps {
  fieldsInfo: {
    fieldType: string;
    id: string;
    isRequired: boolean;
    options?: string[] | object[];
    onChange?: any;
    values?: string[];
    type?: TextFieldProps['inputMode'];
  }[];
}

export interface RootState {
  contactFormInfo: ContactFormInfoInitialState;
}

const FormFieldError = (validationDetails) => {
  const intl = useIntl();
  return (
    <FieldError>
      {({ validationDetails }) => {
        if (validationDetails?.valueMissing) {
          return intl.formatMessage(messages.form_error);
        }
        if (validationDetails?.patternMismatch) {
          return intl.formatMessage(messages.form_pattern_error);
        }
        return '';
      }}
    </FieldError>
  );
};

const GeneralInfoSection = (props: FormSectionProps) => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const { fieldsInfo } = props;

  const [selectedField, setSelectedField] = useState(undefined);

  useEffect(() => {
    dispatch(getContactFormInfo());
  }, [dispatch]);

  const useTypedSelector: TypedUseSelectorHook<RootState> =
    useSelector<RootState>;

  const { data } = useTypedSelector((state) => state.contactFormInfo);

  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const productId = params.get('product_id');
  const country =
    (config.settings.fixedFormCountry as string) || params.get('country');

  return (
    <>
      {fieldsInfo.map((field, index) => {
        if (field.fieldType === 'select') {
          return (
            <div className={styles.fieldGroup} key={index}>
              <div className={styles.field}>
                <Label htmlFor={field.id}>
                  {intl.formatMessage(messages[field.id])}
                  {field.isRequired && ' *'}
                </Label>

                <Select
                  name={field.id}
                  isRequired={field.isRequired}
                  id={field.id}
                  className={cx('react-aria-Select', styles.formSelect)}
                  onSelectionChange={(selected) => setSelectedField(selected)}
                  selectedKey={
                    field.id === 'countries' && country ? country : undefined
                  }
                  placeholder={intl.formatMessage(
                    messages.array_widget_placeholder,
                  )}
                >
                  <FormFieldError />
                  <Button
                    className={cx('react-aria-Button', styles.formSelectButton)}
                    isDisabled={field.id === 'countries' && !!country}
                  >
                    <SelectValue />
                    <ChevronDownSVG />
                  </Button>
                  <Popover isNonModal={true}>
                    <ListBox>
                      {data[field.id]
                        ? data[field.id].map((field) => (
                            <ListBoxItem key={field.token} id={field.token}>
                              {field.title}
                            </ListBoxItem>
                          ))
                        : field.options.map((el, key) => (
                            <ListBoxItem key={key} id={el}>
                              {el}
                            </ListBoxItem>
                          ))}
                    </ListBox>
                  </Popover>
                </Select>

                {field.id === 'new-investment-business-model-location' &&
                  selectedField === 'Undecided' && (
                    <div className={styles.field}>
                      <TextField
                        name={`${field.id}_undecided`}
                        isRequired={true}
                        id={`${field.id}_undecided`}
                        type="text"
                        className={cx(
                          'react-aria-Textfield',
                          styles.textField,
                          styles.additionalMargin,
                        )}
                      >
                        <Label htmlFor={`${field.id}_undecided`}>
                          {intl.formatMessage(messages[field.id])}
                          {field.isRequired && ' *'}
                        </Label>
                        <Input
                          className={cx(
                            'react-aria-Input',
                            styles.input,
                            styles[`input_${field.id}`],
                          )}
                        />
                        <FormFieldError />
                      </TextField>
                    </div>
                  )}
              </div>
            </div>
          );
        }

        if (field.fieldType === 'input') {
          return (
            <div className={styles.field} key={index}>
              <TextField
                name={field.id}
                isRequired={field.isRequired}
                pattern={field.isRequired ? '.*\\S.*' : undefined}
                id={field.id}
                className={cx('react-aria-Textfield', styles.textField)}
                type={field.fieldType}
                inputMode={field.type ?? 'none'}
                maxLength={
                  field.id === 'phone' || field.id === 'phone2' ? 40 : undefined
                }
              >
                <Label htmlFor={field.id}>
                  {intl.formatMessage(messages[field.id])}
                  {field.isRequired && ' *'}
                </Label>
                <FormFieldError />
                <Input
                  className={cx(
                    'react-aria-Input',
                    styles.input,
                    styles[`input_${field.id}`],
                  )}
                  type={
                    field.id === 'email'
                      ? 'email'
                      : field.type === 'numeric'
                        ? 'number'
                        : 'text'
                  }
                />
              </TextField>
            </div>
          );
        }

        if (field.fieldType === 'textarea') {
          return (
            <div
              className={cx(styles.field, styles.textAreaFieldContainer)}
              key={index}
            >
              <TextField
                name={field.id}
                isRequired={field.isRequired}
                id={field.id}
                className={cx('react-aria-Textfield', styles.textField)}
              >
                <Label htmlFor="email">
                  {intl.formatMessage(messages[field.id])}
                  {field.isRequired && ' *'}
                </Label>
                <TextArea
                  className={cx(
                    'react-aria-Textarea',
                    styles.input,
                    styles.textAreaField,
                  )}
                />
                <FormFieldError />
              </TextField>
            </div>
          );
        }

        if (field.fieldType === 'boolean') {
          return (
            <div key={index} className={styles.radioItem}>
              <RadioGroup
                name={field.id}
                isRequired={field.isRequired}
                className={cx('react-aria-RadioGroup', styles.radioGroup)}
              >
                <div className={styles.radioLabelContainer}>
                  <Label className={styles.radioLabel}>
                    {intl.formatMessage(messages[field.id])}
                    {field.isRequired && ' *'}
                  </Label>
                  <FormFieldError />
                </div>
                <div className={styles.radioContainer}>
                  <Radio value="true">
                    {intl.formatMessage(messages.radio_group_yes)}
                  </Radio>
                  <Radio value="false">
                    {intl.formatMessage(messages.radio_group_no)}
                  </Radio>
                </div>
              </RadioGroup>
            </div>
          );
        }

        if (field.fieldType === 'arrayWidget') {
          const selectedProduct =
            data.products?.length > 0 &&
            data.products.find((el) => el.token === productId);

          const choices =
            data[field.id] &&
            data[field.id].length > 0 &&
            data[field.id]?.map((el) => [el.token, el.title]);
          return (
            <div key={index} className={cx(styles.field, styles.arrayWidget)}>
              <Label htmlFor={field.id}>
                {intl.formatMessage(messages[field.id])}
                {field.isRequired && ' *'}
              </Label>
              {field.id === 'products' && !!!productId ? (
                <ArrayWidget
                  id={field.id}
                  value={field.values}
                  onChange={field.onChange}
                  choices={choices}
                  placeholder={intl.formatMessage(
                    messages.array_widget_placeholder,
                  )}
                />
              ) : (
                <TextField
                  name={field.id}
                  id={field.id}
                  type="text"
                  isReadOnly={true}
                  value={selectedProduct?.token}
                  className={cx('react-aria-Textfield', styles.textField)}
                >
                  <Input
                    value={selectedProduct?.title}
                    className={cx('react-aria-Input', styles.input)}
                  />
                </TextField>
              )}
            </div>
          );
        }

        return null;
      })}
    </>
  );
};
export default GeneralInfoSection;

const messages = defineMessages({
  countries: {
    id: 'countries',
    defaultMessage: 'Country',
  },
  firstName: {
    id: 'firstName',
    defaultMessage: 'Name',
  },
  lastName: {
    id: 'lastName',
    defaultMessage: 'Last name',
  },
  centerName: {
    id: 'centerName',
    defaultMessage: 'Center name',
  },
  lanesNumber: {
    id: 'lanesNumber',
    defaultMessage: 'Number of lanes',
  },
  contactAddress: {
    id: 'contactAddress',
    defaultMessage: 'Contact address',
  },
  contactAddress2: {
    id: 'contactAddress2',
    defaultMessage: 'Contact address 2',
  },
  city: {
    id: 'city',
    defaultMessage: 'City',
  },
  zipcode: {
    id: 'zipcode',
    defaultMessage: 'Zipcode',
  },
  phone: {
    id: 'phone',
    defaultMessage: 'Preferred phone',
  },
  phone2: {
    id: 'phone2',
    defaultMessage: 'Alternative phone',
  },
  email: {
    id: 'email',
    defaultMessage: 'Email address',
  },
  jobTitle: {
    id: 'jobTitle',
    defaultMessage: 'Job title',
  },
  occupation: {
    id: 'occupation',
    defaultMessage: 'Occupation',
  },
  products: {
    id: 'products',
    defaultMessage: 'Products of interest',
  },
  notes: {
    id: 'notes',
    defaultMessage: 'Notes',
  },
  location: {
    id: 'location',
    defaultMessage: 'Have you identified a location?',
  },
  land: {
    id: 'land',
    defaultMessage: 'Do you own the land',
  },
  new_building: {
    id: 'new_building',
    defaultMessage: 'Do you plan to build a new building?',
  },
  building: {
    id: 'building',
    defaultMessage: 'Do you own the building?',
  },
  'new-investment-business-model-location': {
    id: 'new-investment-business-model-location',
    defaultMessage:
      'What business model/location have you chosen for your bowling venue?',
  },
  'new-investment-business-model-location_undecided': {
    id: 'new-investment-business-model-location_undecided',
    defaultMessage: 'Alternative option',
  },
  'new-investment-type-of-product': {
    id: 'new-investment-type-of-product',
    defaultMessage: 'What type of bowling product are you interested in?',
  },
  demographic_analysis: {
    id: 'demographic_analysis',
    defaultMessage: 'Have you completed demographic analysis?',
  },
  market_feasibility_study: {
    id: 'market_feasibility_study',
    defaultMessage: 'Have you completed a market feasibility study?',
  },
  cash_flow_projection: {
    id: 'cash_flow_projection',
    defaultMessage: 'Have you prepared a cash flow projection?',
  },
  equity_availability: {
    id: 'equity_availability',
    defaultMessage:
      'Do you have an equity of $22,000-$50,000 per lane available?',
  },
  investors: {
    id: 'investors',
    defaultMessage: 'Are there more than 3 investors?',
  },
  about_project: {
    id: 'about_project',
    defaultMessage: 'Tell us more about your project',
  },
  array_widget_placeholder: {
    id: 'array_widget_placeholder',
    defaultMessage: 'Select an item',
  },
  form_error: {
    id: 'form_error',
    defaultMessage: 'Please fill out this field',
  },
  form_pattern_error: {
    id: 'form_pattern_error',
    defaultMessage:
      'Please enter a valid value. The input cannot consist of spaces only.',
  },
  radio_group_yes: {
    id: 'radio_group_yes',
    defaultMessage: 'Yes',
  },
  radio_group_no: {
    id: 'radio_group_no',
    defaultMessage: 'No',
  },
});
